// ExamenSettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import ParametrageCandidatsExamens from './examens/ParametrageCandidatsExamens';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { Textarea } from './ui/Textarea';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  Loader2,
  FileText,
  DollarSign,
  AlertTriangle,
  X,
  Check,
  Calendar,
  GraduationCap,
  Wallet,
  Users
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "./ui/dialog";
import ConfirmDialog from './ConfirmDialog';
import ChampSelect from './ui/ChampSelect';
import PorteurNotification from './ui/PorteurNotification';
import { useNotification } from '../hooks/useNotification';
import { formaterFCFA } from '../utils/montants';
import { api } from '../services/api';

// Les deux boutons d'un pied de modal ont la meme taille et la meme largeur :
// un bouton d'annulation plus petit suggere une hierarchie qui n'existe pas.
// DESIGN-SYSTEM.md, section Boutons.
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';

const ExamenSettingsPage = ({ onBackToDashboard }) => {
  const [config, setConfig] = useState([]);
  const [documentsRequis, setDocumentsRequis] = useState({
    CEPE: [],
    BEPC: [],
    BAC: []
  });
  const { notification, notifier, masquer } = useNotification();
  // Une lecture qui echoue ne doit pas s'afficher comme un etat vide : sans ce
  // drapeau, `setConfig([])` faisait dire a la page « les frais ne sont pas
  // encore configures », et l'on ressaisissait des frais qui existaient deja.
  const [lectureEchouee, setLectureEchouee] = useState(false);
  // Le rendu passe par ToastPortal et bascule en LongToast si le message est long.
  const showToast = (message, type = 'success') => notifier(message, type);
  // La campagne a laquelle se rapporte ce parametrage : le serveur la renvoie
  // avec la configuration, elle n'est jamais devinee cote client.
  const [anneeScolaireId, setAnneeScolaireId] = useState(null);
  const [anneeScolaireNom, setAnneeScolaireNom] = useState('');
  const [anneesDisponibles, setAnneesDisponibles] = useState([]);
  const [anneeSource, setAnneeSource] = useState('');
  const [recopieEnCours, setRecopieEnCours] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedExamenForDoc, setSelectedExamenForDoc] = useState('CEPE');
  const [selectedExamenForConfig, setSelectedExamenForConfig] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);

  const [newDocument, setNewDocument] = useState({
    nom_document: '',
    description: '',
    obligatoire: true,
    ordre_affichage: 1
  });

  const [configForm, setConfigForm] = useState({
    frais_inscription: 0,
    date_limite_depot: '',
    description: ''
  });

  // Liste des examens disponibles
  const EXAMENS = [
    { code: 'CEPE', nom: 'Certificat d\'Études Primaires Élémentaires' },
    { code: 'BEPC', nom: 'Brevet d\'Études du Premier Cycle' },
    { code: 'BAC', nom: 'Baccalauréat' }
  ];

  useEffect(() => {
    loadConfig();
    loadDocuments();
    loadAnnees();
  }, []);

  // Les autres campagnes servent uniquement de source de recopie.
  const loadAnnees = async () => {
    try {
      const result = await api.anneesScolaires.getAll();
      setAnneesDisponibles(Array.isArray(result?.data) ? result.data : []);
    } catch (error) {
      console.error('Erreur chargement des campagnes:', error);
      notifier("La liste des campagnes n'a pas pu être lue.", 'error');
    }
  };

  const handleRecopier = async () => {
    if (!anneeSource) return;
    setRecopieEnCours(true);
    try {
      const result = await api.examens.copierDocumentsRequis(Number(anneeSource));
      if (result.success) {
        showToast(result.message || 'Pièces recopiées');
        setAnneeSource('');
        loadDocuments();
      } else {
        showToast('Erreur : ' + (result.message || result.error), 'error');
      }
    } catch (error) {
      showToast('Erreur : ' + error.message, 'error');
    } finally {
      setRecopieEnCours(false);
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    try {
      const result = await api.examens.getConfig();
      if (result.success) {
        setConfig(result.data);
        setAnneeScolaireId(result.annee_scolaire_id || null);
        setAnneeScolaireNom(result.annee_scolaire?.nom || '');
        setLectureEchouee(false);
      } else {
        console.error('Erreur chargement config:', result.error);
        setConfig([]);
        setLectureEchouee(true);
        notifier(result.error || "La configuration des examens n'a pas pu être lue.", 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setConfig([]);
      setLectureEchouee(true);
      notifier("La configuration des examens n'a pas pu être lue.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const cepe = await api.examens.getDocumentsRequis('CEPE');
      const bepc = await api.examens.getDocumentsRequis('BEPC');
      const bac = await api.examens.getDocumentsRequis('BAC');

      setDocumentsRequis({
        CEPE: cepe.success ? cepe.data : [],
        BEPC: bepc.success ? bepc.data : [],
        BAC: bac.success ? bac.data : []
      });
    } catch (error) {
      console.error('Erreur chargement documents:', error);
      notifier("La liste des pièces exigées n'a pas pu être lue.", 'error');
    }
  };

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (documentToDelete) {
      setSaving(true);
      const result = await api.examens.deleteDocumentRequis(documentToDelete.id);
      if (result.success) {
        showToast('Pièce supprimée');
        loadDocuments();
      } else {
        showToast('Erreur : ' + result.error, 'error');
      }
      setSaving(false);
    }
    setShowDeleteModal(false);
    setDocumentToDelete(null);
  };

  const handleAddDocumentClick = (examenType) => {
    setSelectedExamenForDoc(examenType);
    setEditingDoc(null);
    setNewDocument({
      nom_document: '',
      description: '',
      obligatoire: true,
      ordre_affichage: 1
    });
    setShowAddDocModal(true);
  };

  const handleEditDocumentClick = (doc, examenType) => {
    setSelectedExamenForDoc(examenType);
    setEditingDoc(doc);
    setNewDocument({
      nom_document: doc.nom_document,
      description: doc.description || '',
      obligatoire: doc.obligatoire,
      ordre_affichage: doc.ordre_affichage
    });
    setShowAddDocModal(true);
  };

  const handleSaveDocument = async () => {
    setSaving(true);
    try {
      let result;

      if (editingDoc) {
        // Modification
        result = await api.examens.updateDocumentRequis(editingDoc.id, newDocument);
      } else {
        // Ajout
        result = await api.examens.addDocumentRequis({
          ...newDocument,
          examen_type: selectedExamenForDoc
        });
      }

      if (result.success) {
        showToast(editingDoc ? 'Pièce modifiée' : 'Pièce ajoutée');
        setShowAddDocModal(false);
        loadDocuments();
        setNewDocument({
          nom_document: '',
          description: '',
          obligatoire: true,
          ordre_affichage: 1
        });
        setEditingDoc(null);
      } else {
        showToast('Erreur : ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Erreur : ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigureFeesClick = (examenCode) => {
    // Trouver l'examen dans la liste
    const examen = EXAMENS.find(e => e.code === examenCode);

    // Chercher la config existante
    const examConfig = config.find(c => c.code === examenCode);

    // Préparer les données du formulaire
    if (examConfig) {
      // Config existe, on charge les données
      setSelectedExamenForConfig({ code: examenCode, nom: examen.nom });
      setConfigForm({
        frais_inscription: examConfig.frais_inscription || 0,
        date_limite_depot: examConfig.date_limite_depot?.split('T')[0] || '',
        description: examConfig.description || ''
      });
    } else {
      // Config n'existe pas, formulaire vide
      setSelectedExamenForConfig({ code: examenCode, nom: examen.nom });
      setConfigForm({
        frais_inscription: 0,
        date_limite_depot: '',
        description: ''
      });
    }

    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const result = await api.examens.updateConfig(selectedExamenForConfig.code, configForm);

      if (result.success) {
        showToast('Configuration enregistrée');
        setShowConfigModal(false);
        loadConfig(); // Recharger pour afficher la nouvelle config
      } else {
        showToast('Erreur : ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Erreur : ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getConfigForExamen = (examenType) => {
    return config.find(c => c.code === examenType);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
        <ChargementSection accent="#246990" libelle="Chargement des paramètres…" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {onBackToDashboard && (
        <EnTeteParametrage
          onRetour={onBackToDashboard}
          icone={GraduationCap}
          titre="Paramétrage des examens nationaux"
          sousTitre="Frais, dates limites et pièces exigées ; classes présentées et dernier examen de l'école"
        />
      )}

      {/* Une lecture qui a echoue le dit, en bandeau et non en toast : un toast
          disparait et laisse une page qui semble simplement vide. */}
      {lectureEchouee && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-red-600" />
              <div>
                <p className="text-sm font-semibold text-red-900">
                  La configuration des examens n'a pas pu être lue
                </p>
                <p className="mt-1 text-xs text-red-700">
                  Ce qui s'affiche ci-dessous n'est pas l'état réel de l'école. Ne
                  ressaisissez pas de frais tant que la lecture n'a pas abouti : vous
                  écraseriez peut-être une configuration existante.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={loadConfig}
              className="h-9 justify-center gap-2 border-red-200 text-red-700 hover:bg-red-100"
            >
              Réessayer
            </Button>
          </div>
        </div>
      )}

      {/* Bandeau de campagne.
          Frais, date limite et pièces exigées sont propres à une année : sans
          cette mention, on ne saurait pas laquelle on est en train de modifier. */}


      {/* Deux sujets, deux onglets (PARAMETRAGE-MODULES.md §2) : ce que coûte
          et demande chaque examen, et qui s'y présente (GO de Daniel du
          01/11/2026). Un seul conteneur blanc autour des onglets. */}
      <Tabs defaultValue="frais" className="w-full">
        <TabsList className="grid grid-cols-2 mb-6 bg-[#246990]/8 p-1 rounded-xl">
          <TabsTrigger
            value="frais"
            className="data-[state=active]:bg-[#246990] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <FileText size={15} className="mr-2" />
            <span>Frais et pièces</span>
          </TabsTrigger>
          <TabsTrigger
            value="candidats"
            className="data-[state=active]:bg-[#319190] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <Users size={15} className="mr-2" />
            <span>Candidats et cycle</span>
          </TabsTrigger>
        </TabsList>

        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
          <TabsContent value="candidats" className="mt-0 focus-visible:outline-none">
            <ParametrageCandidatsExamens />
          </TabsContent>

          <TabsContent value="frais" className="mt-0 focus-visible:outline-none">
      {/* Documents Requis & Frais */}
      <div className="space-y-6">
        {EXAMENS.map((examen) => {
          const examConfig = getConfigForExamen(examen.code);

          return (
            <CarteReglages
              key={examen.code}
              titre={examen.code}
              accent="#246990"
              compteur={`${documentsRequis[examen.code].length} pièce${documentsRequis[examen.code].length > 1 ? 's' : ''
                } exigée${documentsRequis[examen.code].length > 1 ? 's' : ''}`}
            >
              <div>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      Documents requis et configuration des frais
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleConfigureFeesClick(examen.code)}
                      className="flex items-center gap-2 bg-[#246990] text-white rounded-md hover:bg-[#246990]/90 transition-colors"
                    >

                      {examConfig ? 'Modifier les frais' : 'Configurer les frais'}
                    </Button>

                    <Button
                      size="sm"
                      onClick={() => handleAddDocumentClick(examen.code)}
                      className="flex items-center gap-2 bg-[#246990] text-white rounded-md hover:bg-[#246990]/90 transition-colors"
                    >
                      <Plus size={16} className="mr-2" />
                      Ajouter un document
                    </Button>

                  </div>
                </div>

                {/* Affichage des frais actuels */}
                {examConfig ? (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 font-medium">Frais d'inscription</p>
                        <p className="text-lg font-semibold text-[#246990]">
                          {examConfig.frais_inscription?.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) || 0} FCFA
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Date limite</p>
                        <p className="text-lg font-semibold text-[#246990]">
                          {examConfig.date_limite_depot
                            ? new Date(examConfig.date_limite_depot).toLocaleDateString('fr-FR')
                            : 'Non définie'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Documents configurés</p>
                        <p className="text-lg font-semibold text-[#246990]">
                          {documentsRequis[examen.code].length}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800 flex items-start gap-2">
                      <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
                      <span>
                        Les frais ne sont pas encore configurés pour cet examen.
                        Cliquez sur "Configurer les frais" pour les définir.
                      </span>
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-5 border-t border-gray-100 pt-5">
                {documentsRequis[examen.code].length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText size={48} className="mx-auto mb-2 text-gray-300" />
                    <p>Aucun document requis configuré pour {examen.code}</p>
                    <p className="text-sm mt-2">Cliquez sur "Ajouter un document" pour commencer</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {documentsRequis[examen.code]
                      .sort((a, b) => a.ordre_affichage - b.ordre_affichage)
                      .map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded text-gray-600 font-medium">
                              {doc.ordre_affichage}
                            </div>
                            <FileText size={20} className="text-gray-400" />
                            <div className="flex-1">
                              <p className="font-medium">{doc.nom_document}</p>
                              {doc.description && (
                                <p className="text-sm text-gray-500">{doc.description}</p>
                              )}
                            </div>
                            {doc.obligatoire ? (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                Obligatoire
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                Optionnel
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditDocumentClick(doc, examen.code)}
                              className="text-[#246990] hover:bg-blue-50"
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(doc)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </CarteReglages>
          );
        })}
      </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Modal Configuration des Frais */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          {/* En-tete : pastille d'icone, titre, contexte.
              Le DialogContent porte `p-0 gap-0` : c'est donc a chaque partie de
              poser sa marge. Sans elle, le titre collait au bord et les champs
              touchaient les cotes. */}
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Wallet size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Frais de {selectedExamenForConfig?.code}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Frais d'inscription et date limite de dépôt des dossiers
              </DialogDescription>
            </div>
          </DialogHeader>

          {/* Corps : deux colonnes plutot qu'une pile — « large plutot que
              long ». Il porte aussi son propre ascenseur : sans
              `flex-1 overflow-y-auto`, le `overflow-hidden` du conteneur
              coupait le contenu sans qu'on puisse l'atteindre. */}
          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="frais_inscription" className="text-sm text-gray-700">
                  Frais d'inscription *
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="frais_inscription"
                    type="number"
                    min="0"
                    placeholder="15000"
                    value={configForm.frais_inscription}
                    onChange={(e) => setConfigForm({ ...configForm, frais_inscription: e.target.value })}
                    className="h-9 rounded-lg border-gray-200 pr-14 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    FCFA
                  </span>
                </div>
                {Number(configForm.frais_inscription) > 0 && (
                  <p className="mt-1.5 text-[11px] text-gray-400">
                    {formaterFCFA(configForm.frais_inscription)} par candidat
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="date_limite_depot" className="text-sm text-gray-700">
                  Date limite de dépôt
                </Label>
                <Input
                  id="date_limite_depot"
                  type="date"
                  value={configForm.date_limite_depot}
                  onChange={(e) => setConfigForm({ ...configForm, date_limite_depot: e.target.value })}
                  className="mt-1 h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
                <p className="mt-1.5 text-[11px] text-gray-400">
                  Laisser vide si aucune date n'est imposée.
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="description" className="text-sm text-gray-700">
                Description / notes
              </Label>
              <Textarea
                id="description"
                rows={3}
                placeholder="Informations complémentaires sur l'examen"
                value={configForm.description}
                onChange={(e) => setConfigForm({ ...configForm, description: e.target.value })}
                className="mt-1 resize-none rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              />
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4 sm:justify-end sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfigModal(false)}
              className={BOUTON_PIED}
            >
              <X className="h-4 w-4" />
              Annuler
            </Button>

            <Button
              onClick={handleSaveConfig}
              disabled={saving || !configForm.frais_inscription}
              className={BOUTON_PIED + ' bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:bg-gray-300'}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>

      {/* Modal Ajout/Modification Document */}
      <Dialog open={showAddDocModal} onOpenChange={setShowAddDocModal}>
        <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <FileText size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editingDoc ? 'Modifier la pièce exigée' : 'Ajouter une pièce exigée'}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Pièce à fournir par les candidats au {selectedExamenForDoc}
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="grid gap-4 sm:grid-cols-[1fr_140px]">
              <div>
                <Label htmlFor="nom_document" className="text-sm text-gray-700">
                  Nom de la pièce *
                </Label>
                <Input
                  id="nom_document"
                  placeholder="Certificat de naissance"
                  value={newDocument.nom_document}
                  onChange={(e) => setNewDocument({ ...newDocument, nom_document: e.target.value })}
                  className="mt-1 h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>

              <div>
                <Label htmlFor="ordre_affichage" className="text-sm text-gray-700">
                  Ordre
                </Label>
                <Input
                  id="ordre_affichage"
                  type="number"
                  min="1"
                  value={newDocument.ordre_affichage}
                  onChange={(e) => setNewDocument({ ...newDocument, ordre_affichage: parseInt(e.target.value, 10) || 1 })}
                  className="mt-1 h-9 rounded-lg border-gray-200 text-center focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description_doc" className="text-sm text-gray-700">
                Description ou instructions
              </Label>
              <Textarea
                id="description_doc"
                rows={3}
                placeholder="Original ou copie légalisée, moins de trois mois..."
                value={newDocument.description}
                onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                className="mt-1 resize-none rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              />
            </div>

            {/* L'interrupteur de la norme remplace une case a cocher de 16 px,
                que rien ne distinguait d'un champ desactive. */}
            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-800">Pièce obligatoire</p>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  Un dossier incomplet est refusé tant que cette pièce manque.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setNewDocument({ ...newDocument, obligatoire: !newDocument.obligatoire })}
                aria-pressed={newDocument.obligatoire}
                aria-label="Pièce obligatoire"
                className="relative h-6 w-11 flex-shrink-0 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ backgroundColor: newDocument.obligatoire ? '#246990' : '#d1d5db' }}
              >
                <div
                  className={
                    'absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-transform duration-300 ' +
                    (newDocument.obligatoire ? 'translate-x-[22px]' : 'translate-x-[3px]')
                  }
                />
              </button>
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4 sm:justify-end sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddDocModal(false);
                setEditingDoc(null);
              }}
              className={BOUTON_PIED}
            >
              <X className="h-4 w-4" />
              Annuler
            </Button>

            <Button
              onClick={handleSaveDocument}
              disabled={saving || !newDocument.nom_document.trim()}
              className={BOUTON_PIED + ' bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:bg-gray-300'}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {saving ? 'Enregistrement…' : editingDoc ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>

      {/* Modal de confirmation de suppression */}
      {/* ConfirmDialog plutot qu'un Dialog monte a la main. */}
      <ConfirmDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDocumentToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        isLoading={saving}
        type="error"
        title="Retirer cette pièce de la liste ?"
        description={`« ${documentToDelete?.nom_document || 'Cette pièce'} » ne sera plus exigée pour cette campagne. Les campagnes précédentes ne sont pas touchées.`}
        confirmText="Retirer"
        cancelText="Annuler"
      />

      <PorteurNotification notification={notification} onFermer={masquer} />
    </div>
  );
};

export default ExamenSettingsPage;